// src/pages/marketplace/MarketplaceCategoryPage.tsx
// Products in a specific category with breadcrumb, subcategories, and product grid.

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Badge,
  Alert,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

/** Render a row of filled/empty stars for a given rating (0-5). */
function StarRating({ rating }: { rating: number }) {
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`w-4 h-4 ${i < full ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
        </svg>
      ))}
    </span>
  );
}

/** Colored placeholder when product has no icon. */
function ProductIcon({ product }: { product: any }) {
  if (product.icon_url) {
    return (
      <img src={product.icon_url} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
    );
  }
  const bg = product.color_primary || '#6366f1';
  const initial = (product.name ?? '?')[0]?.toUpperCase() ?? '?';
  return (
    <div
      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
      style={{ backgroundColor: bg }}
    >
      {initial}
    </div>
  );
}

type SortOption = 'popularity' | 'rating' | 'newest';

export const MarketplaceCategoryPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortParam = (searchParams.get('sort') || 'popularity') as SortOption;
  const cursor = searchParams.get('cursor') || '';

  const [category, setCategory] = useState<any>(null);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Load category data, subcategories, and products
  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    // We don't have a direct GetCategoryBySlug, so load all categories and find by slug
    const loadData = async () => {
      try {
        const catRes = await store.ListProductCategories();
        const catItems = catRes?.categories?.items;
        const allCategories = Array.isArray(catItems) ? catItems : [];
        const found = allCategories.find((c: any) => c.slug === slug);
        setCategory(found || null);

        if (found) {
          // Load subcategories (children of this category)
          const children = allCategories.filter(
            (c: any) => c.parent?.id === found.entity?.id,
          );
          setSubcategories(children);

          // Load products in this category
          await store.ListProductsByCategory(found.entity?.id ?? '', {
            pagination: { cursor, limit: 20, direction: 'forward' },
          });
          setProducts(store.items ?? []);
        }
      } catch {
        // Errors handled by store
        setProducts(store.items ?? []);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug, cursor]);

  const updateParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams.toString());
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      next.delete('cursor');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  // Client-side sort
  let sortedProducts = [...products];
  if (sortParam === 'rating') {
    sortedProducts.sort(
      (a, b) => (b._marketplace?.average_rating ?? 0) - (a._marketplace?.average_rating ?? 0),
    );
  } else if (sortParam === 'newest') {
    sortedProducts.sort(
      (a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime(),
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (!category) {
    return (
      <Alert variant="warning" title={t('marketplace.categoryNotFound')}>
        <Link to="/marketplace" className="mt-2 inline-block text-sm underline text-primary">
          {t('marketplace.backToMarketplace')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/marketplace" className="text-muted-foreground hover:text-foreground">
          {t('marketplace.title')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{category.name}</span>
      </nav>

      {/* Category header */}
      <div className="flex items-start gap-4">
        {category.icon_url ? (
          <img
            src={category.icon_url}
            alt={category.name}
            className="w-14 h-14 rounded-xl object-contain"
          />
        ) : (
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl"
            style={{ backgroundColor: category.color || '#6366f1' }}
          >
            {(category.name ?? '?')[0]?.toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold text-foreground">{category.name}</h1>
          {category.description && (
            <p className="text-muted-foreground mt-1">{category.description}</p>
          )}
          {category.product_count != null && (
            <p className="text-sm text-muted-foreground mt-1">
              {category.product_count} {t('marketplace.products')}
            </p>
          )}
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Subcategories */}
      {subcategories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {subcategories.map((sub: any) => (
            <Link key={sub.entity?.id || sub.slug} to={`/marketplace/categories/${sub.slug}`}>
              <Badge variant="secondary" size="sm" className="cursor-pointer hover:bg-muted">
                {sub.name}
                {sub.product_count != null && (
                  <span className="ml-1 opacity-60">({sub.product_count})</span>
                )}
              </Badge>
            </Link>
          ))}
        </div>
      )}

      {/* Sort + count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {sortedProducts.length} {t('marketplace.products')}
        </p>
        <Select value={sortParam} onChange={(e) => updateParam('sort', e.target.value)} fullWidth={false}>
          <option value="popularity">{t('marketplace.sortPopularity')}</option>
          <option value="rating">{t('marketplace.sortRating')}</option>
          <option value="newest">{t('marketplace.sortNewest')}</option>
        </Select>
      </div>

      {/* Product grid */}
      {sortedProducts.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('marketplace.noCategoryProducts')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('marketplace.noCategoryProductsHint')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedProducts.map((product: any) => (
            <Link
              key={product.entity?.id || product.slug}
              to={`/marketplace/${product.slug}`}
              className="block group"
            >
              <Card className="h-full transition-shadow group-hover:shadow-md">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4 mb-3">
                    <ProductIcon product={product} />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                        {product.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {product.tagline || product.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <StarRating rating={product._marketplace?.average_rating ?? 0} />
                    {product.is_builtin && (
                      <Badge variant="default" size="sm">
                        {t('marketplace.builtin')}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {sortedProducts.length >= 20 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          {cursor && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const next = new URLSearchParams(searchParams.toString());
                next.delete('cursor');
                setSearchParams(next);
              }}
            >
              {t('common.previous')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const lastItem = sortedProducts[sortedProducts.length - 1];
              if (lastItem) {
                const next = new URLSearchParams(searchParams.toString());
                next.set('cursor', lastItem.entity?.id || '');
                setSearchParams(next);
              }
            }}
          >
            {t('common.next')}
          </Button>
        </div>
      )}
    </div>
  );
});

export default MarketplaceCategoryPage;
